import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from '../utils/auth.utils';
import { NavigationService } from './navigation.service';
import { AmplitudeService } from './amplitude.service';
import { TeamBrandingService } from './team-branding.service';
import { ProjectsService } from './projects.service';
import { LicenseService } from './license.service';
import { ISignInResponse } from '../interfaces/response/sign-in.response';
import { TranslocoService } from '@jsverse/transloco';
import { normalizeLanguage } from '../transloco.config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  AUTH_API_URL = `${environment.API_URL}/${environment.VERSION}/auth`;
  ACCOUNT_API_URL = `${environment.API_URL}/${environment.VERSION}/account`;

  private _http = inject(HttpClient);
  private _navigationService = inject(NavigationService);
  private _amplitudeService = inject(AmplitudeService);
  private _teamBrandingService = inject(TeamBrandingService);
  private _projectsService = inject(ProjectsService);
  private _licenseService = inject(LicenseService);
  private _transloco = inject(TranslocoService);
  private _router = inject(Router);
  public IsLoggedIn: WritableSignal<boolean> = signal(false);

  constructor() {
    this.IsLoggedIn.set(!!this.accessToken);
  }

  set accessToken(token: string) {
    localStorage.setItem('accessToken', token);
  }

  get accessToken(): string {
    return localStorage.getItem('accessToken') ?? '';
  }

  set refreshToken(refreshToken: string) {
    localStorage.setItem('refreshToken', refreshToken);
  }

  get refreshToken(): string {
    return localStorage.getItem('refreshToken') ?? '';
  }

  set userType(userType: string) {
    localStorage.setItem('userType', userType);
  }

  get userType(): string {
    return localStorage.getItem('userType') ?? '';
  }

  isArtistUserType(): boolean {
    const normalized = this.userType.trim().toUpperCase();
    return normalized === 'ARTISTA' || normalized === 'ARTIST';
  }

  isLabelUserType(): boolean {
    const normalized = this.userType.trim().toUpperCase();
    return normalized === 'LABEL';
  }

  signIn(credentials: { username: string; password: string }): Observable<ISignInResponse> {
    return this._http.post<ISignInResponse>(this.AUTH_API_URL + '/token/', credentials).pipe(
      switchMap((response: ISignInResponse) => {
        // A previous account may still be held in memory if its session ended
        // without signOut(); do not let the incoming one inherit it.
        this.clearSessionScopedState();
        this.setSignInResponse(response);
        return this.getAccountProfile().pipe(
          map((profile: any) => {
            this.userType = (profile?.user_type ?? '').toString();
            // Apply the account's language (account_account.language) so the UI
            // matches the account, not whatever stale value is in localStorage.
            const lang = normalizeLanguage(profile?.language ?? null);
            localStorage.setItem('activeLanguage', lang);
            this._transloco.setActiveLang(lang);
            const uuid = profile?.uuid;
            if (uuid) {
              this._amplitudeService.identifyUser(uuid);
              this._amplitudeService.setUserProperties({
                user_type: profile?.user_type,
                club_name: profile?.club?.club_name,
                club_id: profile?.club?.uuid,
                country_code: profile?.country_code,
                contract_signed: profile?.contract_signed,
                is_active: profile?.is_active,
                created_date: profile?.created,
              });
              this._amplitudeService.trackEvent('Login Succeeded', { userType: this.userType });
            }
            return response;
          }),
          catchError((error) => {
            return this.endSession().pipe(
              switchMap(() => throwError(() => error))
            );
          })
        );
      })
    );
  }

  signOut() {
    this._amplitudeService.trackEvent('Logout');
    this._amplitudeService.resetUser();
    // Capture the club slug before clearing the session so we can send the user
    // back to their club's sign-in (e.g. /auth/as-monaco-fc/sign-in).
    const teamSlug = this._teamBrandingService.getStoredTeamSlugOrNull();
    this.endSession();
    this._navigationService.navigateToSignIn(teamSlug);
  }

  /**
   * The API rejected the stored token (expired, revoked, or from another
   * environment). Treat it as a signed-out session: clear it quietly and send
   * the user to sign in only when the current page needs a session.
   */
  handleInvalidToken(): void {
    if (!this.accessToken && !this.refreshToken) { return; }
    const teamSlug = this._teamBrandingService.getStoredTeamSlugOrNull();
    this.endSession();
    // The rejection can arrive mid-navigation (resolvers, first data loads), when
    // router.url still shows the previous page; check where the user is going.
    const target = this._router.getCurrentNavigation()?.finalUrl?.toString()
      ?? this._router.getCurrentNavigation()?.extractedUrl.toString()
      ?? this._router.url;
    if (AuthService.isProtectedUrl(target) || AuthService.isProtectedUrl(this._router.url)) {
      this._navigationService.navigateToSignIn(teamSlug);
    }
  }

  /** Pages behind authGuard; public pages (landing, auth, public profiles, support) are not. */
  private static isProtectedUrl(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    if (/^\/artist\/(profile|my-support)(\/|$)/.test(path)) { return false; }
    return /^\/(brand|label|artist)(\/|$)/.test(path);
  }

  check(): Observable<boolean> {
    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return this.refreshTokenSignIn();
    }

    // Check if the user is logged in
    if (this.IsLoggedIn()) {
      return of(this.IsLoggedIn());
    }

    return of(!!this.accessToken || false);
  }

  private refreshTokenSignIn(): Observable<boolean> {
    if (!this.refreshToken) {
      return this.endSession();
    }
    return this._http.post<ISignInResponse>(this.AUTH_API_URL + '/token/refresh/', { refresh: this.refreshToken }).pipe(
      switchMap((response: ISignInResponse) => {
        this.setSignInResponse(response);
        return of(true);
      }),
      catchError(() => {
        return this.endSession();
      })
    );
  }

  /**
   * Saved tracks and licenses belong to one club. Both services are root
   * singletons, so their state outlives a session unless it is dropped here,
   * and the next account to sign in on this browser inherits it.
   */
  private clearSessionScopedState(): void {
    this._projectsService.clear();
    this._licenseService.clear();
    // Cache written by an earlier build; remove it so browsers that still hold
    // one stop showing another account's saved tracks.
    localStorage.removeItem('acrylic_favorites_cache');
  }

  private setSignInResponse(response: ISignInResponse) {
    // Store the access token in the local storage
    this.accessToken = response.access;
    this.refreshToken = response.refresh;

    // Set the logged in to true
    this.IsLoggedIn.set(true);
  }

  private endSession(): Observable<boolean> {
    // Remove the access token from the local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userType');

    this.clearSessionScopedState();

    // Set the logged in to false
    this.IsLoggedIn.set(false);

    return of(false);
  }

  socialJwtPair(request: any) {
    return this._http.post(`${this.AUTH_API_URL}/social/jwt-pair/`, request)
  }

  getAccountProfile() {
    return this._http.get(`${this.ACCOUNT_API_URL}/profile/`);
  }
}
